import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
} from "@material-tailwind/react";

import { useEffect, useState } from "react";
import { CalendarDaysIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from "react-redux";
import ScrollToTop from "@/components/ScrollToTop";
import ShowLoader from "@/components/Loader";
import Select from "react-select";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import {
    fetchCountry,
    fetchCity,
    fetchOffice,
} from "@/store/slices/internalUsersSlice";
import { Input, Typography } from "@material-tailwind/react";
import { assignRfqToSalesManager, fetchSalesManager, getSalesMemberById, resetStatus } from "@/store/slices/rfqSlice";
import Toaster from "@/components/Toaster";
import { getSettingData } from "@/store/slices/settingSlice";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import useHasPermission from "@/components/useHasPermission";

const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderRadius: '8px',
        minHeight: '44px',
        borderColor: state.isFocused ? '#212121' : '#b0bec5',
        boxShadow: state.isFocused ? '0 0 0 1px #212121' : 'none',
        '&:hover': {
            borderColor: state.isFocused ? '#212121' : '#b0bec5',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#9e9e9e',
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 50,
        borderRadius: '8px',
    })
};
const priorities = [
    {
        label: "High",
        value: "high"
    },
    {
        label: "Medium",
        value: "medium"
    },
    {
        label: "Low",
        value: "low"
    }
];
const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const notBlankRegex = /\S/;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;

const schema = yup.object().shape({
    sales_members: yup.mixed().nullable(),
    country_id: yup.string().required("country_required"),
    city_id: yup.string().required("city_required"),
    office_id: yup.string().required("office_required"),
    sales_representative_id: yup.string().required("sales_manager_required"),
    deadline: yup.string().required("deadline_required"),
    priority: yup.string().required("priority_required"),
    commission_type: yup.string().required("commission_type_required"),
    notes: yup.string()
        .required("note_required")
        .matches(notBlankRegex, "note_empty_error")
        .matches(noHTMLRegex, "HTML tags are not allowed")
        .matches(noCodeRegex, "Code content is not allowed")
        .matches(noConsecutiveSpecialChars, "Note cannot contain consecutive special characters")
        .matches(noLeadingTrailingPunct, "Note cannot start or end with punctuation")
        .min(2, "note_min_length_error")
        .max(100, "note_max_length_error"),
    commission_sales_manager: yup
        .number()
        .typeError("commission_value_required#{\"role\":\"Sales Manager\"}")
        .min(0, "commission_min_value_error")
        .when("commission_type", {
            is: "percentage",
            then: (s) => s.max(100, "commission_percentage_max_error"),
            otherwise: (s) => s.max(99999, "commission_flat_max_error"),
        }),

    commission_sales_rep: yup
        .number()
        .nullable()
        .when("sales_members", {
            is: (value) => value,
            then: (schema) =>
                schema
                    .typeError("commission_value_required#{\"role\":\"Sales Rep\"}")
                    .required("commission_value_required#{\"role\":\"Sales Rep\"}")
                    .min(0, "commission_min_value_error")
                    .when("commission_type", {
                        is: "percentage",
                        then: (s) => s.max(100, "commission_percentage_max_error"),
                        otherwise: (s) =>
                            s.max(99999, "commission_flat_max_error"),
                    }),
            otherwise: (schema) => schema.notRequired(),
        }),

    commission_procurement_manager: yup
        .number()
        .typeError("commission_value_required#{\"role\":\"Procurement Manager\"}")
        .min(0, "commission_min_value_error")
        .when("commission_type", {
            is: "percentage",
            then: (s) => s.max(100, "commission_percentage_max_error"),
            otherwise: (s) => s.max(99999, "commission_flat_max_error"),
        }),

    commission_procurement_rep: yup
        .number()
        .typeError("commission_value_required#{\"role\":\"Procurement Rep\"}")
        .min(0, "commission_min_value_error")
        .when("commission_type", {
            is: "percentage",
            then: (s) => s.max(100, "commission_percentage_max_error"),
            otherwise: (s) => s.max(99999, "commission_flat_max_error"),
        }),

    time_slot_from_sales_manager: yup
        .string()
        .required("timeslot_required#{\"role\":\"Sales Manager\"}")
        .test("from>=now", "timeslot_past_error#{\"role\":\"Sales Manager\"}", function (v) {
            if (!v) return true;
            return new Date(v).getTime() >= new Date().getTime();
        })
        .test("from<=deadline", "timeslot_exceed_deadline_error#{\"role\":\"Sales Manager\",\"direction\":\"From\"}", function (v) {
            const deadline = this.parent?.deadline;
            if (!v || !deadline) return true;
            return new Date(v).getTime() <= new Date(deadline).getTime();
        }),
    // time_slot_to_sales_manager: yup
    //     .string()
    //     .required("Sales Manager time slot is required")
    //     .test("to<=deadline", "Sales Manager slot 'To' cannot exceed the deadline", function (v) {
    //         const deadline = this.parent?.deadline;
    //         if (!v || !deadline) return true;
    //         return new Date(v).getTime() <= new Date(deadline).getTime();
    //     })
    //     .test("to>=from", "Sales Manager slot 'To' cannot be before 'From'", function (v) {
    //         const from = this.parent?.time_slot_from_sales_manager;
    //         if (!v || !from) return true;
    //         return new Date(v).getTime() >= new Date(from).getTime();
    //     }),
    time_slot_to_sales_manager: yup
        .string()
        .when("time_slot_from_sales_manager", {
            is: (value) => !!value,
            then: (schema) =>
                schema
                    .required("timeslot_required#{\"role\":\"Sales Manager\"}")
                    .test(
                        "to<=deadline",
                        "timeslot_exceed_deadline_error#{\"role\":\"Sales Manager\",\"direction\":\"To\"}",
                        function (v) {
                            const deadline = this.parent?.deadline;
                            if (!v || !deadline) return true;
                            return new Date(v).getTime() <= new Date(deadline).getTime();
                        }
                    )
                    .test(
                        "to>=from",
                        "timeslot_to_before_from_error#{\"role\":\"Sales Manager\"}",
                        function (v) {
                            const from = this.parent?.time_slot_from_sales_manager;
                            if (!v || !from) return true;
                            return new Date(v).getTime() >= new Date(from).getTime();
                        }
                    ),
            otherwise: (schema) => schema.notRequired(),
        }),

    time_slot_from_sales_rep: yup
        .string()
        .nullable()
        .when("sales_members", {
            is: (value) => !!value,
            then: (schema) =>
                schema
                    .required("timeslot_required#{\"role\":\"Sales Representative\"}")
                    .test(
                        "from>=now",
                        "timeslot_past_error#{\"role\":\"Sales Representative\"}",
                        function (v) {
                            if (!v) return true;
                            return new Date(v).getTime() >= new Date().getTime();
                        }
                    )
                    .test(
                        "from<=deadline",
                        "timeslot_exceed_deadline_error#{\"role\":\"Sales Representative\",\"direction\":\"From\"}",
                        function (v) {
                            const deadline = this.parent?.deadline;
                            if (!v || !deadline) return true;
                            return new Date(v).getTime() <= new Date(deadline).getTime();
                        }
                    ),
            otherwise: (schema) => schema.notRequired(),
        }),

    // time_slot_to_sales_rep: yup
    // .string()
    // .nullable()
    // .when("sales_members", {
    //     is: (value) => !!value,
    //     then: (schema) =>
    //         schema.when("time_slot_from_sales_rep", {
    //             is: (from) => !!from,
    //             then: (s) =>
    //                 s
    //                     .required("Sales Representative slot 'To' is required")
    //                     .test(
    //                         "to<=deadline",
    //                         "Sales Representative slot 'To' cannot exceed the deadline",
    //                         function (v) {
    //                             const deadline = this.parent?.deadline;
    //                             if (!v || !deadline) return true;
    //                             return new Date(v).getTime() <= new Date(deadline).getTime();
    //                         }
    //                     )
    //                     .test(
    //                         "to>=from",
    //                         "Sales Representative slot 'To' cannot be before 'From'",
    //                         function (v) {
    //                             const from = this.parent?.time_slot_from_sales_rep;
    //                             if (!v || !from) return true;
    //                             return new Date(v).getTime() >= new Date(from).getTime();
    //                         }
    //                     ),
    //             otherwise: (s) => s.notRequired(),
    //         }),
    //     otherwise: (schema) => schema.notRequired(),
    // }),

    time_slot_to_sales_rep: yup
        .string()
        .nullable()
        .when("sales_members", {
            is: (value) => !!value,
            then: (schema) =>
                schema.when("time_slot_from_sales_rep", { // NOSONAR
                    is: (from) => !!from,
                    then: (s) =>
                        s.required("timeslot_required#{\"role\":\"Sales Representative\"}")
                            .test(
                                "to<=deadline",
                                "timeslot_exceed_deadline_error#{\"role\":\"Sales Representative\",\"direction\":\"To\"}",
                                function (v) {
                                    const deadline = this.parent?.deadline;
                                    if (!v || !deadline) return true;
                                    return new Date(v).getTime() <= new Date(deadline).getTime();
                                }
                            )
                            .test(
                                "to>=from",
                                "timeslot_to_before_from_error#{\"role\":\"Sales Representative\"}",
                                function (v) {
                                    const from = this.parent?.time_slot_from_sales_rep;
                                    if (!v || !from) return true;
                                    return new Date(v).getTime() >= new Date(from).getTime();
                                }
                            ),
                    otherwise: (s) => s.notRequired(),
                }),
            otherwise: (schema) => schema.notRequired(),
        }),

    time_slot_from_procurement_manager: yup
        .string()
        .required("timeslot_required#{\"role\":\"Procurement Manager\"}")
        .test("from>=now", "timeslot_past_error#{\"role\":\"Procurement Manager\"}", function (v) {
            if (!v) return true;
            return new Date(v).getTime() >= new Date().getTime();
        })
        .test("from<=deadline", "timeslot_exceed_deadline_error#{\"role\":\"Procurement Manager\",\"direction\":\"From\"}", function (v) {
            const deadline = this.parent?.deadline;
            if (!v || !deadline) return true;
            return new Date(v).getTime() <= new Date(deadline).getTime();
        }),
    // time_slot_to_procurement_manager: yup
    //     .string()
    //     .required("Procurement Manager time slot is required")
    //     .test("to<=deadline", "Procurement Manager slot 'To' cannot exceed the deadline", function (v) {
    //         const deadline = this.parent?.deadline;
    //         if (!v || !deadline) return true;
    //         return new Date(v).getTime() <= new Date(deadline).getTime();
    //     })
    //     .test("to>=from", "Procurement Manager slot 'To' cannot be before 'From'", function (v) {
    //         const from = this.parent?.time_slot_from_procurement_manager;
    //         if (!v || !from) return true;
    //         return new Date(v).getTime() >= new Date(from).getTime();
    //     }),
    time_slot_to_procurement_manager: yup
        .string()
        .when("time_slot_from_procurement_manager", {
            is: (value) => !!value,
            then: (schema) =>
                schema
                    .required("timeslot_required#{\"role\":\"Procurement Manager\"}")
                    .test(
                        "to<=deadline",
                        "timeslot_exceed_deadline_error#{\"role\":\"Procurement Manager\",\"direction\":\"To\"}",
                        function (v) {
                            const deadline = this.parent?.deadline;
                            if (!v || !deadline) return true;
                            return new Date(v).getTime() <= new Date(deadline).getTime();
                        }
                    )
                    .test(
                        "to>=from",
                        "timeslot_to_before_from_error#{\"role\":\"Procurement Manager\"}",
                        function (v) {
                            const from = this.parent?.time_slot_from_procurement_manager;
                            if (!v || !from) return true;
                            return new Date(v).getTime() >= new Date(from).getTime();
                        }
                    ),
            otherwise: (schema) => schema.notRequired(),
        }),

    time_slot_from_procurement_rep: yup
        .string()
        .nullable()
        .test(
            "from>=now",
            "timeslot_past_error#{\"role\":\"Procurement Team Member\"}",
            function (v) {
                if (!v) return true;
                return new Date(v).getTime() >= new Date().getTime();
            }
        )
        .test(
            "from<=deadline",
            "timeslot_exceed_deadline_error#{\"role\":\"Procurement Team Member\",\"direction\":\"From\"}",
            function (v) {
                const deadline = this.parent?.deadline;
                if (!v || !deadline) return true;
                return new Date(v).getTime() <= new Date(deadline).getTime();
            }
        ),

    time_slot_to_procurement_rep: yup
        .string()
        .nullable()
        .when("time_slot_from_procurement_rep", {
            is: (from) => !!from,
            then: (schema) =>
                schema
                    .required("timeslot_required#{\"role\":\"Procurement Team Member\"}")
                    .test(
                        "to<=deadline",
                        "timeslot_exceed_deadline_error#{\"role\":\"Procurement Team Member\",\"direction\":\"To\"}",
                        function (v) {
                            const deadline = this.parent?.deadline;
                            if (!v || !deadline) return true;
                            return new Date(v).getTime() <= new Date(deadline).getTime();
                        }
                    )
                    .test(
                        "to>=from",
                        "timeslot_to_before_from_error#{\"role\":\"Procurement Team Member\"}",
                        function (v) {
                            const from = this.parent?.time_slot_from_procurement_rep;
                            if (!v || !from) return true;
                            return new Date(v).getTime() >= new Date(from).getTime();
                        }
                    ),
            otherwise: (schema) => schema.notRequired(),
        }),

});

// ... existing imports

export function AssignSalesManagerModal({ onClose, rfqId }) {
    const dispatch = useDispatch();
    const [leaveConfirmation, setLeaveConfirmation] = useState(null);
    const { t } = useTranslation('rfq');
    const { t: ct } = useTranslation("common");

    const hasAddMemberPermission = useHasPermission(`member.add`);

    const { settings } = useSelector((state) => state.setting);

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        watch,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: {
            sales_members: null,
            rfq_ids: [rfqId],
            commission_type: "percentage",
            notes: null,
            time_slot_from_sales_manager: "",
            time_slot_to_sales_manager: "",
            time_slot_from_sales_rep: "",
            time_slot_to_sales_rep: "",
            time_slot_from_procurement_manager: "",
            time_slot_to_procurement_manager: "",
            time_slot_from_procurement_rep: "",
            time_slot_to_procurement_rep: "",
        }, resolver: yupResolver(schema)
    });

    const { cities, countries, offices } = useSelector((state) => state.internalUser);
    // Destructure with a default empty array to be safe
    const { sales_managers = [], sales_team = [], loading } = useSelector((state) => state.rfq);

    useEffect(() => {
        dispatch(fetchCountry());
        dispatch(getSettingData());
    }, [dispatch]);

    useEffect(() => {
        if (countries && countries.length > 0) {
            const saudiArabia = countries.find(c => c.label === 'Saudi Arabia');
            if (saudiArabia) {
                setValue('country_id', saudiArabia.value, { shouldValidate: true });
                dispatch(fetchCity(saudiArabia.value));
            }
        }
    }, [countries, setValue, dispatch]);

    const commissionType = watch("commission_type");
    const defaultCommissions = [
        { text: t("commission_sales_member_label"), value: 0, key: "commission_sales_rep", fromKey: "time_slot_from_sales_rep", toKey: "time_slot_to_sales_rep" },
        { text: t("commission_sales_manager_label"), value: 0, key: "commission_sales_manager", fromKey: "time_slot_from_sales_manager", toKey: "time_slot_to_sales_manager" },
        { text: t("commission_procurement_member_label"), value: 0, key: "commission_procurement_rep", fromKey: "time_slot_from_procurement_rep", toKey: "time_slot_to_procurement_rep" },
        { text: t("commission_procurement_manager_label"), value: 0, key: "commission_procurement_manager", fromKey: "time_slot_from_procurement_manager", toKey: "time_slot_to_procurement_manager" },
    ];

    const onSubmit = async (data) => {
        try {
            const payload = {
                ...data,
                sales_members: [selectedUsers],
            };
            const result = await dispatch(assignRfqToSalesManager(payload)).unwrap();
            if (result?.status) {
                Toaster.success(result.message || `RFQ assigned successfully!`);
                dispatch(resetStatus());
                reset();
                onClose();
            } else if (!result?.status && result?.data?.users_on_leave) {
                setLeaveConfirmation(result.data.users_on_leave);
                Toaster.error(result.message || ct("assignOnLeaveError"));
                dispatch(resetStatus());
            } else {
                Toaster.error(result.message || ct("somethingWentWrong"));
                dispatch(resetStatus());
            }
        } catch (err) {
            Toaster.error(err?.message || ct("assignFailedError"));
            dispatch(resetStatus());
        }
    };

    const handleForceAssign = async () => {
        try {
            const payload = {
                ...getValues(),
                sales_members: [selectedUsers],
                force_assign: true,
            };
            const result = await dispatch(assignRfqToSalesManager(payload)).unwrap();
            if (result?.status) {
                Toaster.success(result.message || ct("assignSuccess"));
                dispatch(resetStatus());
                reset();
                onClose();
                setLeaveConfirmation(null);
            } else {
                setLeaveConfirmation(null);
                Toaster.error(result.message || ct("somethingWentWrong"));
                dispatch(resetStatus());
            }
        } catch (err) {
            setLeaveConfirmation(null);
            Toaster.error(err?.message || ct("assignFailedError"));
            dispatch(resetStatus());
        }
    }

    useEffect(() => {
        if (!settings) return;

        const value =
            commissionType === "percentage"
                ? settings.percentage_commission
                : settings.flat_commission;

        reset((prev) => ({
            ...prev,
            commission_sales_manager: value,
            commission_sales_rep: value,
            commission_procurement_manager: value,
            commission_procurement_rep: value,
            // commission_city_manager: value,
            // commission_city_rep: value,
        }));
    }, [settings, commissionType, reset]);


    const now = new Date();
    const minDateTime = now.toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM
    const deadlineValue = watch("deadline");
    const isSalesMemberSelected = !!watch("sales_members");


    const [selectedUsers, setSelectedUsers] = useState(null);

    useEffect(() => {
        let value = watch('time_slot_to_sales_rep');
        setValue('time_slot_from_sales_manager', value);
    }, [watch('time_slot_to_sales_rep')]);

    useEffect(() => {
        let value = watch('time_slot_to_sales_manager');
        setValue('time_slot_from_procurement_rep', value);
        setValue('time_slot_from_procurement_manager', value);
    }, [watch('time_slot_to_sales_manager')]);

    useEffect(() => {
        let value = watch('time_slot_to_procurement_rep');
        setValue('time_slot_from_procurement_manager', value);
    }, [watch('time_slot_to_procurement_rep')]);

    useEffect(() => {
        const deadline = watch("deadline");

        if (!deadline) return;

        setValue("time_slot_to_procurement_manager", deadline, {
            shouldValidate: true,
        });
    }, [watch("deadline"), setValue]);


    return (
        <Dialog
            open={true}
            handler={() => { setLeaveConfirmation(null); onClose(); }}
            size="md"
            className="rounded-2xl p-0"
        >
            {loading && <ShowLoader />}
            <button type="button" className="sr-only" aria-hidden="true" tabIndex={0} />
            <DialogHeader className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-2xl font-bold text-gray-900">
                    {t("assign_sales_manager_title")}
                </h2>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="px-6 max-h-[65vh] overflow-y-auto">
                    <DialogBody>
                        <div className="grid grid-cols-2 md:grid-cols-2 gap-6 mb-6">
                            {/* Country selection */}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium capitalize">
                                    {t("country_label")} <span className="text-red-500">*</span>
                                </Typography>
                                <input type="hidden" {...register('country_id')} />
                                <Select
                                    placeholder={t("select_country_placeholder")}
                                    options={countries}
                                    onChange={(c) => {
                                        setValue("country_id", c?.value, { shouldValidate: true });
                                        dispatch(fetchCity(c?.value));
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                    value={countries.find(c => c.value === watch('country_id')) || null}
                                />
                                {errors.country_id && (
                                    <p className="text-red-500 text-sm">{t(errors.country_id.message)}</p>
                                )}
                            </div>

                            {/* City selection */}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium capitalize">
                                    {t("city_label")} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t("select_city_placeholder")}
                                    options={cities}
                                    onChange={(c) => {
                                        setValue("city_id", c?.value, { shouldValidate: true });
                                        dispatch(fetchOffice(c?.value));
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                />
                                {errors.city_id && (
                                    <p className="text-red-500 text-sm">{t(errors.city_id.message)}</p>
                                )}
                            </div>

                            {/* Office selection */}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium capitalize">
                                    {t("office_label")} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t("select_office_placeholder")}
                                    options={offices}
                                    onChange={(o) => {
                                        setValue("office_id", o?.value, { shouldValidate: true });
                                        if (o?.value) dispatch(fetchSalesManager({ office_id: o?.value, type: 'Sales Representative' }));
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                />
                                {errors.office_id && (
                                    <p className="text-red-500 text-sm">{t(errors.office_id.message)}</p>
                                )}
                            </div>

                            {/* Sales Manager selection */}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium capitalize">
                                    {t("sales_manager_label")} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t("select_sales_manager_placeholder")}
                                    options={sales_managers}
                                    onChange={(m) => {
                                        setValue("sales_representative_id", m?.value, {
                                            shouldValidate: true,
                                        });
                                        setSelectedUsers(null);
                                        dispatch(getSalesMemberById({ parent_id: m?.value, type: 'Sales Representative' }));
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                />
                                {errors.sales_representative_id && (
                                    <p className="text-red-500 text-sm">
                                        {t(errors.sales_representative_id.message)}
                                    </p>
                                )}
                            </div>
                            {sales_team && <div className="col-span-2">
                                <Typography variant="small" className="mb-2 font-medium capitalize">
                                    {t("sales_team_members_label")}
                                </Typography>
                                <Select
                                    isDisabled={!hasAddMemberPermission}
                                    placeholder={t("select_sales_member_placeholder")}
                                    options={sales_team}
                                    onChange={(m) => {
                                        if (m?.value === undefined) {
                                            setValue('time_slot_from_sales_rep', "");
                                            setValue('time_slot_to_sales_rep', "");
                                        }
                                        setValue("sales_members", m ? m.value : null, { shouldValidate: true });
                                        setSelectedUsers(m ? m.value : null);
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                    noOptionsMessage={() => t("no_sales_member_available")}
                                    // This is the critical fix:
                                    value={sales_team?.find(option => option.value === selectedUsers) || null}
                                />
                                {/* {sales_team.map((member) => (
                                    <label
                                        key={member.user_id}
                                        className="flex items-center gap-3 p-2 border rounded cursor-pointer hover:bg-gray-50"
                                    >
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 text-green-600"
                                            checked={selectedUsers.includes(member.user_id)}
                                            onChange={() => handleChange(member.user_id)}
                                        />

                                        <img
                                            src={member.profile}
                                            alt={member.name}
                                            className="w-10 h-10 rounded-full object-cover"
                                        />

                                        <div className="flex-1">
                                            <p className="font-medium">{member.name}</p>
                                            <p className="text-sm text-gray-500">
                                                RFQs: {member.rfqs}
                                            </p>
                                        </div>
                                    </label>
                                ))} */}
                            </div>}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium capitalize">
                                    {t("rfq_deadline_label")} <span className="text-red-500">*</span>
                                </Typography>
                                <Input
                                    type="datetime-local"
                                    min={minDateTime}
                                    {...register("deadline")}
                                    className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                                    labelProps={{ className: "before:content-none after:content-none" }}
                                />
                                {errors.deadline && (
                                    <p className="text-red-500 text-sm">{t(errors.deadline.message)}</p>
                                )}
                            </div>

                            {/* Priority */}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium capitalize">
                                    {t("rfq_priority_label")} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t("select_priority_placeholder")}
                                    options={priorities}
                                    onChange={(priority) =>
                                        setValue("priority", priority?.value, { shouldValidate: true })
                                    }
                                    styles={customSelectStyles}
                                    isClearable
                                />
                                {errors.priority && (
                                    <p className="text-red-500 text-sm">{t(errors.priority.message)}</p>
                                )}
                            </div>
                        </div>

                        {/* Commission Section */}
                        <div className="grid grid-cols-1 gap-6 mb-6">
                            <div>
                                <Typography variant="small" className="mb-2 font-medium capitalize">
                                    {t("set_commission_label")}
                                </Typography>
                                <div className="flex items-center gap-6 mb-3">
                                    <label className="flex items-center gap-2 text-sm">
                                        <input type="radio" value="percentage" {...register("commission_type")} />
                                        {t("percentage_commission_label")}
                                    </label>
                                    <label className="flex items-center gap-2 text-sm">
                                        <input type="radio" value="flat" {...register("commission_type")} />
                                        {t("flat_commission_label")}
                                    </label>
                                </div>

                                <div className="space-y-4">
                                    {defaultCommissions.map((item, index) => (
                                        <div key={index} className="flex flex-col gap-1">
                                            <div className="bg-gray-50 rounded-md">

                                                <div className="flex items-center justify-between px-4 py-2">
                                                    <p className="text-sm text-gray-800">{item.text}</p>
                                                    <div className="flex items-center gap-3 flex-wrap justify-end">
                                                        <div className="flex items-center gap-2">
                                                            {/* <input
                                                            step="any"
                                                            type="number"
                                                            min={"0"}
                                                            {...register(item.key)}
                                                            className={`w-20 text-center border rounded-md py-1 text-sm ${errors[item.key] ? "border-red-500" : "border-gray-300"}`}
                                                        /> */}
                                                            <input
                                                                step="any"
                                                                type="number"
                                                                min="0"
                                                                {...register(item.key)}
                                                                disabled={item.key === "commission_sales_rep" && !isSalesMemberSelected}
                                                                className={`w-20 text-center border rounded-md py-1 text-sm ${errors[item.key] ? "border-red-500" : "border-gray-300"
                                                                    } ${item.key === "commission_sales_rep" && !isSalesMemberSelected
                                                                        ? "bg-gray-100 cursor-not-allowed"
                                                                        : ""
                                                                    }`}
                                                            />
                                                            <span className="text-gray-600 text-sm">
                                                                {watch("commission_type") === "percentage" ? "%" : "SAR"}
                                                            </span>
                                                        </div>

                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between px-4 py-2">
                                                    <span className="text-gray-600 text-xs whitespace-nowrap capitalize">
                                                        {t("timeslot_label_simple")}
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        {/* <input
                                                        type="datetime-local"
                                                        disabled={!deadlineValue}
                                                        min={minDateTime}
                                                        max={deadlineValue || undefined}
                                                        {...register(item.fromKey)}
                                                        className={`w-[175px] text-center border rounded-md py-1 text-sm ${errors[item.fromKey] ? "border-red-500" : "border-gray-300"}`}
                                                    /> */}
                                                        <input
                                                            type="datetime-local"
                                                            disabled={
                                                                !deadlineValue ||
                                                                (item.key === "commission_sales_rep" && !isSalesMemberSelected)
                                                            }
                                                            min={minDateTime}
                                                            max={deadlineValue || undefined}
                                                            {...register(item.fromKey)}
                                                            className={`w-[175px] text-center border rounded-md py-1 text-sm ${errors[item.fromKey] ? "border-red-500" : "border-gray-300"
                                                                } ${item.key === "commission_sales_rep" && !isSalesMemberSelected
                                                                    ? "bg-gray-100 cursor-not-allowed"
                                                                    : ""
                                                                }`}
                                                        />
                                                        {/* <input
                                                        type="datetime-local"
                                                        disabled={!deadlineValue}
                                                        min={watch(item.fromKey) || minDateTime}
                                                        max={deadlineValue || undefined}
                                                        {...register(item.toKey)}
                                                        className={`w-[175px] text-center border rounded-md py-1 text-sm ${errors[item.toKey] ? "border-red-500" : "border-gray-300"}`}
                                                    /> */}
                                                        <input
                                                            type="datetime-local"
                                                            disabled={
                                                                !deadlineValue ||
                                                                (item.key === "commission_sales_rep" && !isSalesMemberSelected)
                                                            }
                                                            min={watch(item.fromKey) || minDateTime}
                                                            max={deadlineValue || undefined}
                                                            {...register(item.toKey)}
                                                            className={`w-[175px] text-center border rounded-md py-1 text-sm ${errors[item.toKey] ? "border-red-500" : "border-gray-300"
                                                                } ${item.key === "commission_sales_rep" && !isSalesMemberSelected
                                                                    ? "bg-gray-100 cursor-not-allowed"
                                                                    : ""
                                                                }`}
                                                        />
                                                        {(watch(item.fromKey) || watch(item.toKey)) && (
                                                            <XCircleIcon
                                                                className="h-5 w-5 text-red-500 cursor-pointer hover:text-red-700 transition-colors"
                                                                onClick={() => {
                                                                    setValue(item.fromKey, "", { shouldValidate: true });
                                                                    setValue(item.toKey, "", { shouldValidate: true });
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            {errors[item.key] && (
                                                <p className="text-red-500 text-xs px-4">{t(errors[item.key].message.split('#')[0], { role: JSON.parse(errors[item.key].message.split('#')[1] || '{}')?.role })}</p>
                                            )}
                                            {(errors[item.fromKey] || errors[item.toKey]) && (
                                                <p className="text-red-500 text-xs px-4">
                                                    {t((errors[item.fromKey] || errors[item.toKey])?.message.split('#')[0], { role: JSON.parse((errors[item.fromKey] || errors[item.toKey])?.message.split('#')[1] || '{}')?.role, direction: JSON.parse((errors[item.fromKey] || errors[item.toKey])?.message.split('#')[1] || '{}')?.direction })}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="block text-sm font-medium mb-1 capitalize">
                                    {t("note_label")} <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    rows="3"
                                    className="w-full border rounded-lg px-3 py-2"
                                    {...register("notes")}
                                    placeholder={t("note_placeholder")}
                                ></textarea>
                                {errors.notes && (
                                    <p className="text-red-500 text-sm">{t(errors.notes.message)}</p>
                                )}
                            </div>
                        </div>
                    </DialogBody>
                    {/* Important Commission Notice */}
                    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
                        <p className="text-sm font-medium text-amber-900">
                            {t("important_notice_title")}
                        </p>

                        <p className="mt-1 text-sm text-amber-800 leading-relaxed">
                            {t("commission_deduction_notice")}
                        </p>
                    </div>
                </div>

                {leaveConfirmation?.length > 0 && (
                    <div className="mx-6 mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 shadow-sm">
                        {/* Header with Warning Icon */}
                        <div className="flex items-center gap-2 mb-3">
                            <svg
                                className="h-5 w-5 text-amber-600 flex-shrink-0"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                                />
                            </svg>
                            <h3 className="font-semibold text-amber-800">
                                {t("schedule_conflict_title")}
                            </h3>
                        </div>

                        {/* User List */}
                        <ul className="divide-y divide-amber-200/60 rounded-md bg-amber-100/50 px-3 py-1 mb-3">
                            {leaveConfirmation.map((user) => (
                                <li
                                    key={user.user_id}
                                    className="flex flex-col sm:flex-row sm:items-center justify-between py-2 text-sm gap-1 sm:gap-4"
                                >
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="font-medium text-gray-900">{user.name}</span>
                                        {(user.user_type || user.role) && (
                                            <span className="inline-flex items-center rounded-md bg-amber-200/70 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                                                {[user.user_type, user.role].filter(Boolean).join(" • ")}
                                            </span>
                                        )}
                                    </div>

                                    <div className="text-xs sm:text-sm font-medium text-amber-700 whitespace-nowrap">
                                        {moment(user.leave_from).format("DD MMM YYYY")} –{" "}
                                        {moment(user.leave_to).format("DD MMM YYYY")}
                                    </div>
                                </li>
                            ))}
                        </ul>

                        {/* Actionable Footer Copy */}
                        <p className="text-sm text-amber-800">
                            <Trans t={t} i18nKey="force_assign_notice">
                                To proceed, select available users or enable <strong className="font-semibold underline decoration-amber-500 underline-offset-2">Force Assign</strong> to override their leave status.
                            </Trans>
                        </p>
                    </div>
                )}
                <DialogFooter className="flex justify-end gap-4 border-t pt-4">
                    <button type="button" onClick={onClose} className="px-5 py-2 rounded-lg text-gray-700 hover:bg-gray-100">
                        {ct("notNowButton")}
                    </button>
                    <button type="submit" className="px-5 py-2 rounded-lg bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-medium shadow-sm">
                        {t("assign_now_button")}
                    </button>
                    {leaveConfirmation?.length > 0 && (
                        <button
                            type="button"
                            className="px-5 py-2 rounded-lg bg-red-400 hover:bg-red-500 text-white font-medium shadow-sm"
                            onClick={() => handleForceAssign()}
                        >
                            {t("force_assign_button")}
                        </button>
                    )}
                </DialogFooter>
            </form>
        </Dialog>
    );
}

export default AssignSalesManagerModal;
